import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import './Auth.css';

function RegisterForm({ onRegisterSuccess, hideHeading = false }) {
  const { t } = useTranslation(['landing', 'shared']);
  const oauthEnabled = false;
  const phoneAuthEnabled = false;
  const [authMethod, setAuthMethod] = useState('email'); // 'email' lub 'phone'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleEmailPasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: name.trim(),
          },
        },
      });

      if (signUpError) throw signUpError;

      if (data.user) {
        onRegisterSuccess(data.user);
      }
    } catch (err) {
      setError(err.message || t('auth.register.error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneOTPRequest = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t('auth.register.nameRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        phone: phone,
        options: {
          channel: 'sms',
          data: {
            name: name.trim(),
          },
        },
      });

      if (otpError) throw otpError;

      setOtpSent(true);
    } catch (err) {
      setError(err.message || t('auth.register.errorOtpSend'));
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneOTPVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        phone: phone,
        token: otp,
        type: 'sms',
      });

      if (verifyError) throw verifyError;

      if (data.user) {
        onRegisterSuccess(data.user);
      }
    } catch (err) {
      setError(err.message || t('auth.register.errorOtpVerify'));
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthRegister = async (provider) => {
    setLoading(true);
    setError(null);

    try {
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: `${window.location.origin}`,
        },
      });

      if (oauthError) throw oauthError;
    } catch (err) {
      setError(err.message || t('auth.register.error'));
      setLoading(false);
    }
  };

  return (
    <div className="auth-form-container">
      {!hideHeading && <h2>{t('auth.register.title')}</h2>}

      {/* OAuth buttons */}
      <div className="oauth-buttons">
        <button
          type="button"
          className="oauth-button oauth-google"
          onClick={() => handleOAuthRegister('google')}
          disabled={loading || !oauthEnabled}
          title={!oauthEnabled ? t('auth.todoTitle') : undefined}
        >
          <svg className="oauth-icon" viewBox="0 0 24 24" width="20" height="20">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {t('auth.register.continueWithGoogle')}
          {!oauthEnabled ? ` (${t('auth.todoLabel')})` : ''}
        </button>

        <button
          type="button"
          className="oauth-button oauth-facebook"
          onClick={() => handleOAuthRegister('facebook')}
          disabled={loading || !oauthEnabled}
          title={!oauthEnabled ? t('auth.todoTitle') : undefined}
        >
          <svg className="oauth-icon" viewBox="0 0 24 24" width="20" height="20" fill="#1877F2">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          {t('auth.register.continueWithFacebook')}
          {!oauthEnabled ? ` (${t('auth.todoLabel')})` : ''}
        </button>
      </div>

      <div className="auth-divider">
        <span>{t('common.or')}</span>
      </div>

      {/* Method selector */}
      <div className="auth-method-selector">
        <button
          type="button"
          className={`method-tab ${authMethod === 'email' ? 'active' : ''}`}
          onClick={() => {
            setAuthMethod('email');
            setOtpSent(false);
            setError(null);
          }}
        >
          {t('auth.methods.email')}
        </button>
        <button
          type="button"
          className={`method-tab ${authMethod === 'phone' ? 'active' : ''}`}
          disabled={!phoneAuthEnabled}
          onClick={() => {
            setAuthMethod('phone');
            setOtpSent(false);
            setError(null);
          }}
          title={!phoneAuthEnabled ? t('auth.todoTitle') : undefined}
        >
          {t('auth.methods.phone')}
          {!phoneAuthEnabled ? ` (${t('auth.todoLabel')})` : ''}
        </button>
      </div>

      {error && <div className="auth-error">{error}</div>}

      {/* Email/Password form */}
      {authMethod === 'email' && (
        <form onSubmit={handleEmailPasswordSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="name">{t('auth.register.name')}</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={loading}
              minLength={2}
              maxLength={40}
              placeholder={t('auth.register.namePlaceholder')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('auth.register.email')}</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              placeholder={t('auth.login.emailPlaceholder')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('auth.register.password')}</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              minLength={6}
              placeholder={t('auth.register.passwordHint')}
            />
          </div>

          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading ? t('auth.register.submitting') : t('auth.register.submit')}
          </button>
        </form>
      )}

      {/* Phone OTP form */}
      {authMethod === 'phone' && (
        <form 
          onSubmit={otpSent ? handlePhoneOTPVerify : handlePhoneOTPRequest} 
          className="auth-form"
        >
          <div className="form-group">
            <label htmlFor="name">{t('auth.register.name')}</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={loading || otpSent}
              minLength={2}
              maxLength={40}
              placeholder={t('auth.register.namePlaceholder')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">{t('auth.register.phone')}</label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              disabled={loading || otpSent}
              placeholder={t('auth.register.phonePlaceholder')}
              pattern="\+[0-9]{10,15}"
            />
            <small className="form-hint">{t('auth.register.phoneFormat')}</small>
          </div>

          {otpSent && (
            <div className="form-group">
              <label htmlFor="otp">{t('auth.register.otpSent')}</label>
              <input
                id="otp"
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                required
                disabled={loading}
                placeholder={t('auth.register.otpPlaceholder')}
                maxLength={6}
                pattern="[0-9]{6}"
              />
              <small className="form-hint">{t('auth.register.otpHint')}</small>
            </div>
          )}

          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading 
              ? (otpSent ? t('auth.register.verifying') : t('auth.register.sending')) 
              : (otpSent ? t('auth.register.verifyOtp') : t('auth.register.sendOtp'))
            }
          </button>

          {otpSent && (
            <button
              type="button"
              className="link-button-small"
              onClick={() => {
                setOtpSent(false);
                setOtp('');
                setError(null);
              }}
            >
              {t('auth.register.changeNumber')}
            </button>
          )}
        </form>
      )}
    </div>
  );
}

export default RegisterForm;
